import {
  Box,
  Button,
  InputGroup as ChakraInputGroup,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  InputRightElement,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { CustomCollapse, InputFormControl, NumberFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import type {
  CalcAmountRealtyFlatRequest,
  CalcMinAmountRealtyRequest,
  InsuranceParamsPropertyDto,
  SecondaryRealtyDto
} from 'BrokerTsLib/src/dtos'
import {
  Insurer,
  Selection,
  SelectionType
} from 'BrokerTsLib/src/enums'
import {
  useFieldArray,
  useFormContext
} from 'BrokerTsLib/src/rhf'
import React, { useEffect, useState } from 'react'

import { SwitchControl } from '@/components/SwitchControl'
import { HouseFloorSelector } from '@/containers/HouseFloorSelector'
import { useCalcRecommendedAmountRealty, useCalcRecommendedFlatAmountRealty } from '@/hooks/useWizard'
import { Calculate, Cloud, QuestionMark, Trash } from '@/icons'

import { FormGroup } from '../../../components/FormGroup'
import { FormLabel } from '../../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../../components/FormSection'
import { FormSpacer } from '../../../components/FormSpacer'
import { InputGroup } from '../../../components/InputGroup'
import { RadioStack } from '../../../components/RadioStack'
import {
  useInsurers,
  useSelections
} from '../../../hooks'
import { CustomNumberInput } from '../../CustomNumberInput'

export function RealtyForm() {
  const { data: dataOwner } = useSelections(SelectionType.Vlastnictvi)
  const { data: dataApparmentType } = useSelections(SelectionType.TypBytovehoDomu)
  const { data: dataBasementArea } = useSelections(SelectionType.PlochaPodsklepení)
  const { data: dataStrechy } = useSelections(SelectionType.TypStrechy)
  const { data: dataDokonceni } = useSelections(SelectionType.DokonceniNemovitosti)
  const { data: dataTypKonstrukce } = useSelections(SelectionType.TypKonstrukce)
  const { data: dataStav } = useSelections(SelectionType.Stav)
  const { data: dataStavInterieru } = useSelections(SelectionType.StavInterieru)
  const { data: dataDispoziceBytu } = useSelections(SelectionType.DispoziceBytu)
  const { data: dataCarParking } = useSelections(SelectionType.TypParkovaniVozidla)
  const { data: dataBuildingType } = useSelections(SelectionType.DalsiNemovitost)
  const { isInsurerAvailable, isInsurersAvailable } = useInsurers(false, null)

  const { mutateAsync: calcRecommendedRealtyFlatAmount, isPending: isCalculatingRecommendedRealtyFlatAmount } = useCalcRecommendedFlatAmountRealty()
  const { mutateAsync: calcRecommendedRealtyAmount } = useCalcRecommendedAmountRealty()

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    control
  } = methods

  const [isCalcFlatOpen, setIsCalcFlatOpen] = useState(false)
  const [calcRealtyFlatAmountMsg, setCalcRealtyFlatAmountMsg] = useState('')
  const [calcRealtyAmountError, setCalcRealtyAmountError] = useState('')

  const isContracted = !!getValues('generalParams.contractNumber')
  const selectedRealtyType = watch('property.data.realtyTypeId')
  const houseWithBasementValue = watch('property.data.hasBasement')
  const roofType = watch('property.data.realty.roofType')
  const residentialRoof = watch('property.data.realty.isRoofResidential')
  const floorsAboveGroundValue = watch('property.data.realty.floorsCount')
  const realtyFlatOwner = watch('property.data.realty.owner')
  const appartmentOnLastFloor = watch('property.data.realty.isApartmentOnLastFloor')
  const realtyHasLoggia = watch('property.data.realty.hasLoggia')
  const realtyHasTerrace = watch('property.data.realty.hasTerrace')
  const realtyHasGarden = watch('property.data.realty.hasGarden')
  const realtyHasParking = watch('property.data.realty.hasParking')
  const secondaryRealty = watch('property.data.realty.secondaryRealty')

  const isOpenApartmentOwned = !!selectedRealtyType && selectedRealtyType == Selection.Byt
  const isOpenApartment = !!selectedRealtyType && (selectedRealtyType == Selection.Byt || selectedRealtyType == Selection.NajemniByt)
  const isOpenBasement = houseWithBasementValue == true
  const isOpenRoofSedlova = roofType == Selection.StrechaSedlova
  const isOpenRoofNotResidential = residentialRoof == false
  const isVlastnictviDruzstevni = realtyFlatOwner == Selection.VlastnictviDruzstevni
  const isAppartmentOnLastFloor = appartmentOnLastFloor == true
  const hasLoggia = realtyHasLoggia == true
  const hasTerrace = realtyHasTerrace == true
  const hasGarden = realtyHasGarden == true
  const hasParking = realtyHasParking == true

  let atticAreaTooltip = ''
  if (isInsurerAvailable(Insurer.Kooperativa) || isInsurerAvailable(Insurer.Csob) || isInsurerAvailable(Insurer.KB)) {
    let insurers = ''
    if (isInsurerAvailable(Insurer.Kooperativa)) {
      insurers += ', Kooperativa'
    }
    if (isInsurerAvailable(Insurer.Csob)) {
      insurers += ', ČSOB'
    }
    if (isInsurerAvailable(Insurer.KB)) {
      insurers += ', KB pojišťovna'
    }
    atticAreaTooltip = 'Pojišťovna ' + insurers.substring(2) + ' nezahrnuje do užitné plochy podkroví, které není obytné.'
  }

  const {
    fields: fieldsSecondaryRealties,
    remove: removeSecondaryRealty,
    append: appendSecondaryRealty,
  } = useFieldArray({
    name: 'property.data.secondaryRealties',
    control: control,
  })

  const calcRealtyFlatAmount = () => {
    const req: CalcAmountRealtyFlatRequest = {
      usefulArea: getValues('property.data.realty.usefulArea'),
      addressCode: getValues('property.data.address.addressPoint'),
      approvedYear: getValues('property.data.realty.approvedYear'),
      interiorState: getValues('property.data.realty.interiorState'),
      flatDisposition: getValues('property.data.realty.flatDisposition'),
      ownership: getValues('property.data.realty.owner'),
      annuity: getValues('property.data.realty.annuity'),
      floor: getValues('property.data.realty.apartmentFloor'),
      topFloor: getValues('property.data.realty.isApartmentOnLastFloor'),
      attic: getValues('property.data.realty.isAppartmentInAttic'),
      hasCellar: getValues('property.data.realty.hasCellar'),
      isPanel: getValues('property.data.realty.isPanel'),
      elevator: getValues('property.data.realty.elevator'),
    }
    if (getValues('property.data.realty.hasLoggia') == true) {
      req.loggiaArea = getValues('property.data.realty.loggiaArea')
    }
    if (getValues('property.data.realty.hasTerrace') == true) {
      req.terraceArea = getValues('property.data.realty.terraceArea')
    }
    if (getValues('property.data.realty.hasGarden') == true) {
      req.gardenArea = getValues('property.data.realty.gardenArea')
    }
    if (getValues('property.data.realty.hasParking') == true) {
      req.parkingType = getValues('property.data.realty.parkingType')
      req.numberOfParkings = getValues('property.data.realty.numberOfParkings')
    }
    calcRecommendedRealtyFlatAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.realty.realtyAmount', resp.data.amount!)
        setCalcRealtyFlatAmountMsg('Hodnota nemovitosti byla nastavena na vypočtenou hodnotu.')
        setCalcRealtyAmountError('')
      } else {
        setCalcRealtyFlatAmountMsg('')
        setCalcRealtyAmountError(resp.data.message)
      }
    })
  }

  const calcRealtyAmount = () => {
    const req: CalcMinAmountRealtyRequest = {
      builtUpArea: getValues('property.data.realty.builtUpArea'),
      builtUpCadastralArea: getValues(
        'property.data.realty.builtUpCadastralArea',
      ),
      usefulArea: getValues('property.data.realty.usefulArea'),
      realtyType: getValues('property.data.realtyTypeId'),
      appartmentType: getValues('property.data.realty.apparmentType'),
      hasBasement: getValues('property.data.hasBasement'),
      basementArea: getValues('property.data.realty.basementArea'),
      floorCount: getValues('property.data.realty.floorsCount'),
      roofType: getValues('property.data.realty.roofType'),
      isRoofResidential: getValues('property.data.realty.isRoofResidential'),
      finishState: getValues('property.data.realty.finishState'),
      structuralType: getValues('property.data.realty.structuralType'),
    }

    calcRecommendedRealtyAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.realty.realtyAmount', resp.data.amount!)
        setCalcRealtyAmountError('')
      } else {
        setCalcRealtyAmountError(resp.data.message)
      }
    })
  }

  useEffect(() => {
    if (secondaryRealty && fieldsSecondaryRealties.length == 0)
      appendSecondaryRealty({} as SecondaryRealtyDto)
  }, [secondaryRealty, fieldsSecondaryRealties.length])

  return (
    <FormSection>
      <FormSectionHeader>
        <FormSectionTitle>Nemovitost</FormSectionTitle>

        <FormSectionDescription>Trvale obývaná.</FormSectionDescription>
      </FormSectionHeader>

      <FormSectionBody>
        <CustomCollapse in={isOpenApartmentOwned}>
          <FormControl isInvalid={!!errors.property?.data?.realty?.owner} isRequired>
            <FormLabel tooltip={<>
              Osobní vlastnictví bytu - pojištěný je zapsán jako
              vlastník v katastru nemovitostí.
              <br />
              Družstevní vlastnictví bytu - vlastníkem družstevního
              bytu je vždy družstvo (bytové, stavební bytové apod.),
              které s družstevníkem, tedy nájemcem uzavírá nájemní
              smlouvu. Některé pojišťovny umožňují takovému
              nájemníkovi pojistit nejen domácnost, ale i stavbu -
              bytovou jednotku. V takovém případě se jedná o
              pojištění cizího pojistného nebezpečí. Vlastník musí
              být s touto pojistnou smlouvou seznámen a případné
              právo na pojistné plnění má jen v případě, prokáže-li,
              že vlastníka bytu/jednotky, tzn. družstvo, se smlouvou
              seznámil a družstvo jako vlastník pojištěné věci s
              poskytnutím pojistného plnění vyjádřilo svůj souhlas.
            </>}>
              Vlastnictví
            </FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.realty.owner')?.toString()} onChange={x => setValue('property.data.realty.owner', parseInt(x))}>
              <RadioStack variant="stackedTile">
                {dataOwner?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                  {item.description}
                </Radio>)}
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.realty?.owner && <FormErrorMessage>
              {errors.property?.data?.realty?.owner.message}
            </FormErrorMessage>}
          </FormControl>
        </CustomCollapse>

        <CustomCollapse in={isOpenApartment}>
          <FormControl isInvalid={!!errors.property?.data?.realty?.apparmentType} isRequired>
            <FormLabel tooltip="Typové bytové domy jsou domy realizované hromadnou výstavbou.">
              Bytový dům
            </FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.realty.apparmentType')?.toString()} onChange={x => setValue('property.data.realty.apparmentType', parseInt(x))}>
              <RadioStack variant="stackedTile">
                {dataApparmentType?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                  {item.description}
                </Radio>)}
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.realty?.apparmentType && <FormErrorMessage>
              {errors.property?.data?.realty?.apparmentType.message}
            </FormErrorMessage>}
          </FormControl>
        </CustomCollapse>

        <FormGroup alignItems="stretch">
          <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.isApproved}>
            <FormLabel tooltip="Zkolaudováno">Zkolaudováno</FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.realty.isApproved') == true ? 'yes' : watch('property.data.realty.isApproved') == false ? 'no' : undefined} onChange={val => setValue('property.data.realty.isApproved', val === 'yes')}>
              <RadioStack variant="stackedTile">
                <Radio value="yes">Ano</Radio>
                <Radio value="no">Ne</Radio>
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.realty?.isApproved && <FormErrorMessage>
              {errors.property?.data?.realty?.isApproved.message}
            </FormErrorMessage>}
          </FormControl>

          <FormControl isInvalid={!!errors.property?.data?.realty?.approvedYear} isRequired>
            <FormLabel tooltip="Uveďte rok kolaudace/uvedení do provozu nebo rok poslední rekolaudace.">
              Rok kolaudace či výstavby
            </FormLabel>

            <InputGroup isDisabled={isContracted} htmlSize={4} {...register('property.data.realty.approvedYear')} errors={errors.property?.data?.realty?.approvedYear} />
          </FormControl>
        </FormGroup>

        <FormSpacer />

        <FormGroup>
          <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.builtUpArea}>
            <FormLabel tooltip="Součet plochy všech podlaží, včetně balkonů, teras, sklepů a půdních prostor, bez ohledu na to, zda jsou využívány">
              Celková podlahová plocha
            </FormLabel>

            <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.builtUpArea')} onValueChange={(_, value) => setValue('property.data.realty.builtUpArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
              placeholder: 'Např. 128'
            }} />

            {errors.property?.data?.realty?.builtUpArea && <FormErrorMessage>
              {errors.property?.data?.realty?.builtUpArea.message}
            </FormErrorMessage>}
          </FormControl>

          <FormControl>
            <FormLabel tooltip="z toho určeno k podnikání">
              z toho určeno k podnikání
            </FormLabel>

            <CustomNumberInput w={32} value={watch('property.data.realty.builtUpAreaBusiness') ?? undefined} onValueChange={(_, value) => setValue('property.data.realty.builtUpAreaBusiness', value)} isDisabled={isContracted} inputRightElement="%" inputProps={{
              placeholder: 'Např. 20'
            }} />
          </FormControl>
        </FormGroup>

        <FormGroup sx={{
          alignItems: 'end'
        }}>
          <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.builtUpCadastralArea}>
            <FormLabel tooltip="Plocha ohraničená pravoúhlými průměty vnějšího líce obvodových konstrukcí všech nadzemních i podzemních podlaží do vodorovné roviny. Je uvedena v katastru nemovitostí.">
              Zastavěná plocha
            </FormLabel>

            <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.builtUpCadastralArea')} onValueChange={(_, value) => setValue('property.data.realty.builtUpCadastralArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
              placeholder: 'Např. 128'
            }} />

            {errors.property?.data?.realty?.builtUpCadastralArea && <FormErrorMessage>
              {errors.property?.data?.realty?.builtUpCadastralArea.message}
            </FormErrorMessage>}
          </FormControl>

          <CustomCollapse in={isOpenApartment}>
            <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.usefulArea}>
              <FormLabel tooltip="Do užitné plochy se nezahrnuje balkón, lodžie, terasa, sklep, zahrádka atd.">
                Užitná plocha
              </FormLabel>

              <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.usefulArea')}
                onValueChange={(_, value) => setValue('property.data.realty.usefulArea', value)}
                isDisabled={isContracted} inputRightElement="㎡"
                inputProps={{
                  placeholder: 'Např. 128',
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    setValue('property.data.household.livingArea', e.target.value as unknown as number)
                  }
                }}
              />

              {errors.property?.data?.realty?.usefulArea && <FormErrorMessage>
                {errors.property?.data?.realty?.usefulArea.message}
              </FormErrorMessage>}
            </FormControl>
          </CustomCollapse>

          {
            /* <Button
                         as="a"
                         href="https://intranet.okgroup.cz/okg-informace/13664"
                         target="_blank"
                         rel="noopener"
                         variant="outline"
                         isDisabled={isContracted}
                        >
                         Vyhledat v katastru nemovitostí <External color="secondary.500" />
                        </Button> */
          }
        </FormGroup>

        <CustomCollapse in={!isOpenApartment}>
          <FormSpacer />
          <Flex sx={{
            columnGap: 32,
            rowGap: 8,
            placeItems: 'start',
            flexWrap: 'wrap'
          }}>
            <Box sx={{
              display: 'grid',
              gap: 4
            }}>
              <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.floorsCount}>
                <FormLabel tooltip="Do počtu nadzemních podlaží se nezapočítává podkroví.">
                  Počet nadzemních podlaží
                </FormLabel>

                <InputGroup placeholder="Např. 1" {...register('property.data.realty.floorsCount')} errors={errors.property?.data?.realty?.floorsCount} isDisabled={isContracted} />
              </FormControl>

              <CustomCollapse in={isOpenBasement}>
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.basementArea}>
                  <FormLabel tooltip="Plocha podsklepení">
                    Plocha podsklepení
                  </FormLabel>
                  <Select {...register('property.data.realty.basementArea')} defaultValue={''} isDisabled={isContracted}>
                    <option disabled value="">
                      Vyberte plochu podsklepení
                    </option>
                    {dataBasementArea?.map(item => <option value={item.id.toString()} key={item.id.toString()}>
                      {item.description}
                    </option>)}
                  </Select>

                  {errors.property?.data?.realty?.basementArea && <FormErrorMessage>
                    {errors.property?.data?.realty?.basementArea.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <FormControl isInvalid={!!errors.property?.data?.realty?.roofType} isRequired>
                <FormLabel tooltip={<>
                  Vyberte typ provedení střechy budovy.
                  <br />
                  Plochá střecha - střešní konstrukce nebo střecha
                  se sklonem nanejvýš 5°.
                  <br />
                  Sedlová střecha - všechny ostatní střechy
                  (označované jako např. sedlové, pultové, stanové,
                  mansardové, apod.), které nemůžeme označit jako
                  ploché. V prostoru pod sklonitou střechou se
                  nachází podkroví, které je umístěné nad posledním
                  nadzemním podlažím budovy.
                </>}>
                  Typ střechy
                </FormLabel>

                <RadioGroup
                  name={register('property.data.realty.roofType').name}
                  value={watch('property.data.realty.roofType')?.toString()}
                  onChange={val => setValue('property.data.realty.roofType', Number(val))}
                  isDisabled={isContracted}
                >
                  <RadioStack variant="stackedTile">
                    {dataStrechy?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                      {item.description}
                    </Radio>)}
                  </RadioStack>
                </RadioGroup>

                {errors.property?.data?.realty?.roofType && <FormErrorMessage>
                  {errors.property?.data?.realty?.roofType.message}
                </FormErrorMessage>}
              </FormControl>

              <CustomCollapse in={isOpenRoofSedlova}>
                <FormControl isInvalid={!!errors.property?.data?.realty?.isRoofResidential} isRequired>
                  <FormLabel tooltip="Je podkroví obytné">
                    Je podkroví obytné
                  </FormLabel>

                  <RadioGroup isDisabled={isContracted} value={watch('property.data.realty.isRoofResidential') == true ? 'yes' : watch('property.data.realty.isRoofResidential') == false ? 'no' : undefined} onChange={val => setValue('property.data.realty.isRoofResidential', val === 'yes')}>
                    <RadioStack variant="stackedTile">
                      <Radio value="yes">Ano</Radio>
                      <Radio value="no">Ne</Radio>
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.realty?.isRoofResidential && <FormErrorMessage>
                    {errors.property?.data?.realty?.isRoofResidential.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <CustomCollapse in={isOpenRoofSedlova && isOpenRoofNotResidential &&
                (isInsurerAvailable(Insurer.Kooperativa) || isInsurerAvailable(Insurer.Csob) || isInsurerAvailable(Insurer.KB))}>
                <FormControl isInvalid={!!errors.property?.data?.realty?.atticArea}>
                  <FormLabel tooltip={atticAreaTooltip}>
                    Plocha podkroví
                  </FormLabel>

                  <ChakraInputGroup maxW={32}>
                    <Input placeholder="Např. 128" {...register('property.data.realty.atticArea')} isDisabled={isContracted} />
                    <InputRightElement children="㎡" />
                  </ChakraInputGroup>

                  {errors.property?.data?.realty?.atticArea && <FormErrorMessage>
                    {errors.property?.data?.realty?.atticArea.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <FormControl isInvalid={!!errors.property?.data?.realty?.finishState} isRequired>
                <FormLabel tooltip={<Stack gap={2}>
                  <Text>
                    Rozhodujte podle toho, které prvky převažují.
                  </Text>
                  <Text>
                    <Text fontWeight="bold" mr={1}>
                      Základní provedení
                    </Text>{' '}
                    jednotlivých konstrukcí se vyznačuje nižší
                    kvalitou materiálu, základy jsou bez izolací,
                    chybí elektroinstalace, bleskosvod, rozvody
                    vody, koupelna, WC je mimo budovu.
                  </Text>
                  <Text>
                    <Text fontWeight="bold" mr={1}>
                      Standardní provedení
                    </Text>{' '}
                    jednotlivých konstrukcí využívá standardní
                    materiály, obsahuje standardní vnitřní vybavení,
                    jako jsou vana či sprchový kout, splachovací
                    záchod.
                  </Text>
                  <Text>
                    <Text fontWeight="bold" mr={1}>
                      Luxusní provedení
                    </Text>{' '}
                    jednotlivých konstrukcí se vyznačuje zateplenou
                    fasádou a vnějšími obklady, vnitřní vybavení je
                    z exotických a nadstandardních materiálů.
                  </Text>
                </Stack>}>
                  Provedení budovy
                </FormLabel>

                <RadioGroup name={register('property.data.realty.finishState').name} value={watch('property.data.realty.finishState')?.toString()} onChange={val => setValue('property.data.realty.finishState', Number(val))} isDisabled={isContracted}>
                  <RadioStack variant="stackedTile">
                    {dataDokonceni?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                      {item.description}
                    </Radio>)}
                  </RadioStack>
                </RadioGroup>

                {errors.property?.data?.realty?.finishState && <FormErrorMessage>
                  {errors.property?.data?.realty?.finishState.message}
                </FormErrorMessage>}
              </FormControl>
            </Box>

            {!isContracted && <Box>
              <HouseFloorSelector floors={floorsAboveGroundValue ? parseInt(floorsAboveGroundValue.toString()) : 0} basement={!!houseWithBasementValue} roof={residentialRoof ?? false} showRoof={isOpenRoofSedlova} onRoofChange={(selected: boolean) => {
                setValue('property.data.realty.isRoofResidential', selected)
              }} onFloorsChange={(floors: number) => setValue('property.data.realty.floorsCount', floors)} onBasementChange={(isSelected: boolean) => {
                setValue('property.data.hasBasement', isSelected)
              }} />
            </Box>}
          </Flex>
        </CustomCollapse>



        <CustomCollapse in={isOpenApartment}>
          <FormSpacer />

          <Box sx={{
            display: 'grid',
            gap: 4
          }}>
            <FormControl isInvalid={!!errors.property?.data?.realty?.apartmentNumber} isRequired>
              <FormLabel>Č. bytu</FormLabel>

              <ChakraInputGroup maxW={48}>
                <Input placeholder="Např. 513" {...register('property.data.realty.apartmentNumber')} isDisabled={isContracted}
                  onBlur={(e) => setValue('property.data.household.apartmentNumber', e.target.value)} />
              </ChakraInputGroup>

              {errors.property?.data?.realty?.apartmentNumber && <FormErrorMessage>
                {errors.property?.data?.realty?.apartmentNumber.message}
              </FormErrorMessage>}
            </FormControl>

            <FormGroup>
              <FormControl>
                <FormLabel tooltip="Pro domácnost v přízemí, sklepním bytě nebo vyvýšeném přízemí uveďte hodnotu 0, jinak uveďte číslo patra. Pokud je domácnost ve více podlažích, uveďte to nejnižší. Hodnota může být vyžadována pojišťovnou pro určení rizika povodně.">
                  Poschodí
                </FormLabel>

                <ChakraInputGroup maxW={48}>
                  <Input placeholder="Např. 5" {...register('property.data.realty.apartmentFloor')} isDisabled={isContracted} />
                </ChakraInputGroup>
              </FormControl>

              {(isInsurerAvailable(Insurer.Axa) || isInsurerAvailable(Insurer.GeneraliCeskaPojistovna)) && <FormControl>
                <FormLabel tooltip="Zaškrtněte, pokud je byt v posledním poschodí bytového domu">
                  Poslední patro
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.isApartmentOnLastFloor')} isChecked={watch('property.data.realty.isApartmentOnLastFloor') ?? false} isDisabled={isContracted} />
                  <FormLabel htmlFor={'property.data.realty.isApartmentOnLastFloor'} variant="switch">
                    Je v posledním poschodí
                  </FormLabel>
                </SwitchControl>
              </FormControl>}
            </FormGroup>
          </Box>

          <FormSpacer />
        </CustomCollapse>

        <FormControl isInvalid={!!errors.property?.data?.realty?.structuralType} isRequired>
          <FormLabel tooltip={<>
            Vyberte typ konstrukce.
            <br />
            Pro pojišťovnu UNIQA:
            <br />
            Provedení Zdivo a Smíšené se vyznačuje nosnou konstrukcí
            z nehořlavých či převážně z nehořlavých materiálů (např.
            cihla, beton, panel) a nehořlavou střechou (např. pálené
            tašky, plech, eternit, nehořlavý plast apod.). Dřevěnou
            konstrukcí se rozumím především dřevostavby a stavby z
            jiných hořlavých materiálů, nebo stavby takového
            provedení, kde hořlavé konstrukce převládají z více než
            50 %.
          </>}>
            Typ konstrukce
          </FormLabel>

          <RadioGroup name={register('property.data.realty.structuralType').name} value={watch('property.data.realty.structuralType')?.toString()} onChange={val => setValue('property.data.realty.structuralType', Number(val))} isDisabled={isContracted}>
            <RadioStack variant="stackedTile">
              {dataTypKonstrukce?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                {item.description}
              </Radio>)}
            </RadioStack>
          </RadioGroup>

          {
            errors.property?.data?.realty?.structuralType && <FormErrorMessage>
              {errors.property?.data?.realty?.structuralType.message}
            </FormErrorMessage>
          }
        </FormControl >

        <FormControl isInvalid={!!errors.property?.data?.realty?.state} isRequired>
          <FormLabel tooltip="Vyberte stav nemovitosti">
            Stav nemovitosti
          </FormLabel>

          <RadioGroup name={register('property.data.realty.state').name} value={watch('property.data.realty.state')?.toString()} onChange={val => setValue('property.data.realty.state', Number(val))} isDisabled={isContracted}>
            <RadioStack variant="stackedTile">
              {dataStav?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                {item.description}
              </Radio>)}
            </RadioStack>
          </RadioGroup>

          {
            errors.property?.data?.realty?.state && <FormErrorMessage>
              {errors.property?.data?.realty?.state.message}
            </FormErrorMessage>
          }
        </FormControl >

        {
          isInsurersAvailable([Insurer.Axa, Insurer.CPP, Insurer.Csob, Insurer.Slavia, Insurer.Kooperativa, Insurer.GeneraliCeskaPojistovna, Insurer.Allianz, Insurer.Direct, Insurer.Pillow, Insurer.KB]) && <FormControl isInvalid={!!errors.property?.data?.realty?.goodState} isRequired>
            <FormLabel tooltip="Vyberte zda je budova v dobrém technickém stavu. Budovy ve špatném technickém stavu není možné pojistit.">
              Dobrý technický stav
            </FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.realty.goodState') == true ? 'yes' : watch('property.data.realty.goodState') == false ? 'no' : undefined} onChange={val => setValue('property.data.realty.goodState', val === 'yes')}>
              <RadioStack variant="stackedTile">
                <Radio value="yes">Ano</Radio>
                <Radio value="no">Ne</Radio>
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.realty?.goodState && <FormErrorMessage>
              {errors.property?.data?.realty?.goodState.message}
            </FormErrorMessage>}
          </FormControl>
        }

        < CustomCollapse in={isCalcFlatOpen}>
          <Box sx={{
            borderRadius: 'md',
            borderColor: 'gray.400',
            borderWidth: 'thin',
            p: 4,
            width: '100%'
          }}>

            <FormGroup>

              <CustomCollapse in={isVlastnictviDruzstevni}>
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.annuity}>
                  <FormLabel tooltip="Uveďte hodnotu 0, pokud je anuita již splacená">
                    Anuita
                  </FormLabel>

                  <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.annuity')} onValueChange={(_, value) => setValue('property.data.realty.annuity', value)} isDisabled={isContracted} inputRightElement="Kč" inputProps={{
                    placeholder: 'Např. 500000'
                  }} />

                  {errors.property?.data?.realty?.annuity && <FormErrorMessage>
                    {errors.property?.data?.realty?.annuity.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <FormControl>
                <FormLabel tooltip="Vyberte, pokud se jedná o panelovou stavbu">
                  Panelový dům
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.isPanel')} isChecked={watch('property.data.realty.isPanel') ?? false} isDisabled={isContracted} />

                </SwitchControl>
              </FormControl>

              <FormControl>
                <FormLabel>
                  Výtah
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.elevator')} isChecked={watch('property.data.realty.elevator') ?? false} isDisabled={isContracted} />

                </SwitchControl>
              </FormControl>

              <CustomCollapse in={isAppartmentOnLastFloor}>
                <FormControl>
                  <FormLabel>
                    Půdní prostor / podkroví
                  </FormLabel>
                  <SwitchControl>
                    <Switch {...register('property.data.realty.isAppartmentInAttic')} isChecked={watch('property.data.realty.isAppartmentInAttic') ?? false} isDisabled={isContracted} />
                    <FormLabel htmlFor={'property.data.realty.isApartmentInAttic'} variant="switch">
                      Je byt umístěn v podkroví
                    </FormLabel>
                  </SwitchControl>
                </FormControl>
              </CustomCollapse>
            </FormGroup>
            <FormGroup sx={{
              mt: 4
            }}>
              <FormControl isRequired>
                <FormLabel>
                  Stav interiéru
                </FormLabel>

                {(dataStavInterieru?.length ?? 0) > 0 && <Select {...register('property.data.realty.interiorState')} isDisabled={isContracted} defaultValue={getValues('property.data.realty.interiorState') ?? ''}>
                  <option value=''>Vyberte</option>
                  {dataStavInterieru?.map(item => <option value={item.id?.toString()} key={item.id}>{item.description}</option>)}
                </Select>}

                {errors.property?.data?.realty?.interiorState && <FormErrorMessage>
                  {errors.property?.data?.realty?.interiorState?.message}
                </FormErrorMessage>}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  Dispozice bytu
                </FormLabel>

                {(dataDispoziceBytu?.length ?? 0) > 0 && <Select {...register('property.data.realty.flatDisposition')} isDisabled={isContracted} defaultValue={getValues('property.data.realty.flatDisposition') ?? ''}>
                  <option value=''>Vyberte</option>
                  {dataDispoziceBytu?.map(item => <option value={item.id?.toString()} key={item.id}>{item.description}</option>)}
                </Select>}

                {errors.property?.data?.realty?.flatDisposition && <FormErrorMessage>
                  {errors.property?.data?.realty?.flatDisposition?.message}
                </FormErrorMessage>}
              </FormControl>

            </FormGroup>
            <FormGroup sx={{
              mt: 4
            }}>

              <FormControl>
                <FormLabel tooltip="Vyberte, pokud k bytu přísluší balkon nebo lodžie">
                  Balkon
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.hasLoggia')} isChecked={watch('property.data.realty.hasLoggia') ?? false} isDisabled={isContracted} />
                </SwitchControl>
              </FormControl>

              <CustomCollapse in={hasLoggia}>
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.loggiaArea}>
                  <FormLabel>
                    Velikost balkonu
                  </FormLabel>

                  <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.loggiaArea')} onValueChange={(_, value) => setValue('property.data.realty.loggiaArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
                    placeholder: 'Např. 12'
                  }} />

                  {errors.property?.data?.realty?.loggiaArea && <FormErrorMessage>
                    {errors.property?.data?.realty?.loggiaArea.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <FormControl>
                <FormLabel tooltip="Vyberte, pokud k bytu přísluší terasa">
                  Terasa
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.hasTerrace')} isChecked={watch('property.data.realty.hasTerrace') ?? false} isDisabled={isContracted} />
                </SwitchControl>
              </FormControl>

              <CustomCollapse in={hasTerrace}>
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.terraceArea}>
                  <FormLabel>
                    Velikost terasy
                  </FormLabel>

                  <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.terraceArea')} onValueChange={(_, value) => setValue('property.data.realty.terraceArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
                    placeholder: 'Např. 20'
                  }} />

                  {errors.property?.data?.realty?.terraceArea && <FormErrorMessage>
                    {errors.property?.data?.realty?.terraceArea.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <FormControl>
                <FormLabel tooltip="Vyberte, pokud k bytu přísluší sklepní prostor">
                  Sklep
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.hasCellar')} isChecked={watch('property.data.realty.hasCellar') ?? false} isDisabled={isContracted} />

                </SwitchControl>
              </FormControl>

              <FormControl>
                <FormLabel tooltip="Vyberte, pokud k bytu přísluší zahrada">
                  Zahrada
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.hasGarden')} isChecked={watch('property.data.realty.hasGarden') ?? false} isDisabled={isContracted} />
                </SwitchControl>
              </FormControl>

              <CustomCollapse in={hasGarden}>
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.gardenArea}>
                  <FormLabel>
                    Velikost zahrady
                  </FormLabel>

                  <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.gardenArea')} onValueChange={(_, value) => setValue('property.data.realty.gardenArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
                    placeholder: 'Např. 50'
                  }} />

                  {errors.property?.data?.realty?.gardenArea && <FormErrorMessage>
                    {errors.property?.data?.realty?.gardenArea.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

              <FormControl>
                <FormLabel tooltip="Vyberte, pokud k bytu přísluší parkování pro vozidla">
                  Parkování
                </FormLabel>
                <SwitchControl>
                  <Switch {...register('property.data.realty.hasParking')} isChecked={watch('property.data.realty.hasParking') ?? false} isDisabled={isContracted} />
                </SwitchControl>
              </FormControl>

              <CustomCollapse in={hasParking}>
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.numberOfParkings}>
                  <FormLabel>
                    Počet parkovacích míst
                  </FormLabel>

                  <InputGroup isDisabled={isContracted} htmlSize={4} {...register('property.data.realty.numberOfParkings')} errors={errors.property?.data?.realty?.numberOfParkings} />
                </FormControl>
              </CustomCollapse>

              <CustomCollapse in={hasParking}>
                <FormControl isRequired>
                  <FormLabel>
                    Vlastní parkování
                  </FormLabel>

                  <RadioGroup name={register('property.data.realty.parkingType').name} value={watch('property.data.realty.parkingType')?.toString()} onChange={val => setValue('property.data.realty.parkingType', Number(val))} isDisabled={isContracted}>
                    <RadioStack variant="stackedTile">
                      {dataCarParking?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                        {item.description}
                      </Radio>)}
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.realty?.parkingType && <FormErrorMessage>
                    {errors.property?.data?.realty?.parkingType?.message}
                  </FormErrorMessage>}
                </FormControl>
              </CustomCollapse>

            </FormGroup>
            <FormGroup sx={{
              mt: 4,
              alignItems: 'center'
            }}>
              <Button type="button" variant="outline" isDisabled={isContracted} onClick={() => {
                calcRealtyFlatAmount()
              }} isLoading={isCalculatingRecommendedRealtyFlatAmount}>
                <Cloud color="secondary.500" />
                Určit tržní hodnotu bytu
              </Button>

              {calcRealtyFlatAmountMsg && <Text>{calcRealtyFlatAmountMsg}</Text>}

              {calcRealtyAmountError && <Text variant={'error'}>{calcRealtyAmountError}</Text>}
            </FormGroup>
          </Box >

        </CustomCollapse >

        <FormSpacer />

        <FormGroup sx={{
          alignItems: 'end'
        }}>
          <FormControl isInvalid={!!errors.property?.data?.realty?.realtyAmount} isRequired>
            <FormLabel tooltip="Zadejte hodnotu nemovitosti. Do hodnoty se započítávají zdi, omítky, podlahy, rozvody, topení, kuchyňská linka, dveře, koupelnové vybavení...">
              Hodnota nemovitosti
            </FormLabel>

            <CustomNumberInput w={48} defaultValue={getValues('property.data.realty.realtyAmount')} onValueChange={(_, value) => setValue('property.data.realty.realtyAmount', value)} isDisabled={isContracted} inputRightElement="Kč" inputProps={{
              placeholder: 'Např. 10 000 000'
            }} />

            {errors.property?.data?.realty?.realtyAmount && <FormErrorMessage>
              {errors.property?.data?.realty?.realtyAmount.message}
            </FormErrorMessage>}
          </FormControl>

          <CustomCollapse in={!isOpenApartment}>
            <Flex sx={{
              gap: 4,
              alignItems: 'center'
            }}>
              <Button variant="outline" isDisabled={isContracted} onClick={() => calcRealtyAmount()}>
                <Calculate color="secondary.500" />
                Orientační poj. částka
              </Button>

              <Tooltip shouldWrapChildren label="Jedná se o orientační pojistnou částku vypočítanou dle obecného vzorce, se započítaným nárůstem cen stavebních prací a materiálů, za kterou neneseme odpovědnost">
                <IconButton variant="ghost" aria-label="">
                  <QuestionMark />
                </IconButton>
              </Tooltip>

              {calcRealtyAmountError && <Text variant={'error'}>{calcRealtyAmountError}</Text>}
            </Flex>
          </CustomCollapse>

          <CustomCollapse in={isOpenApartment && !isCalcFlatOpen}>
            <Flex sx={{
              gap: 4,
              alignItems: 'center'
            }}>
              <Button variant="outline" //isDisabled={isContracted} // má být viditelné i po sjednání
                onClick={() => setIsCalcFlatOpen(true)}>
                <Calculate color="secondary.500" />
                Orientační poj. částka
              </Button>

              <Tooltip shouldWrapChildren label="Jedná se o odhad tržní ceny za pomocí využití online kalkulačky odhad-zdarma.cz. Jedná se o hodnotu, za kterou neneseme žádnou odpovědnost, výslednou částku vždy konzultujte s klientem.">
                <IconButton variant="ghost" aria-label="">
                  <QuestionMark />
                </IconButton>
              </Tooltip>

              {calcRealtyAmountError && <Text variant={'error'}>{calcRealtyAmountError}</Text>}
            </Flex>
          </CustomCollapse>
        </FormGroup>

        <FormControl isInvalid={!!errors.property?.data?.realty?.secondaryRealty} isRequired>
          <FormLabel tooltip="Vyberte zda chcete pojistit vedlejší budovy. Pokud bude zvoleno NE, budou v nabídkách zobrazeny limity na pojištění vedlejších staveb zdarma dle aktuálních podmínek pojišťoven.">
            Vedlejší budovy
          </FormLabel>

          <RadioGroup value={watch('property.data.realty.secondaryRealty') == true ? 'yes' : watch('property.data.realty.secondaryRealty') == false ? 'no' : undefined} onChange={val => setValue('property.data.realty.secondaryRealty', val === 'yes')} isDisabled={isContracted}>
            <RadioStack variant="stackedTile">
              <Radio value="yes">Ano</Radio>
              <Radio value="no">Ne</Radio>
            </RadioStack>
          </RadioGroup>

          {errors.property?.data?.realty?.secondaryRealty && <FormErrorMessage>
            {errors.property?.data?.realty?.secondaryRealty.message}
          </FormErrorMessage>}
        </FormControl>

        <CustomCollapse in={secondaryRealty == true}>
          <Box sx={{
            display: 'grid',
            gap: 4
          }}>
            {fieldsSecondaryRealties.map((field, index) => <React.Fragment key={field.id}>
              <Divider borderColor="gray" />

              <Box sx={{
                display: 'grid',
                gap: 4
              }}>
                <FormGroup>
                  <SelectFormControl label="Typ stavby"
                    name={`property.data.secondaryRealties.${index}.type`} control={control}
                    options={dataBuildingType?.map(item => ({ value: item.id.toString(), label: item.description! })) ?? []}
                    isDisabled={isContracted}
                    unselectedText='Vyberte typ'
                  />

                  <NumberFormControl label="Hodnota nemovitosti"
                    name={`property.data.secondaryRealties.${index}.secondaryRealtyAmount`} control={control}
                    isDisabled={isContracted}
                    inputRightElement="Kč" inputProps={{ placeholder: 'Např. 3 000 000' }} isRequired
                  />
                </FormGroup>

                <FormGroup sx={{
                  alignItems: 'end'
                }}>
                  <InputFormControl label="Popis"
                    name={`property.data.secondaryRealties.${index}.description`} control={control}
                    isDisabled={isContracted}
                  />

                  {!isContracted && fieldsSecondaryRealties.length > 1 && <Flex sx={{
                    gap: 4,
                    alignItems: 'center'
                  }}>
                    <Button variant="outline" onClick={() => removeSecondaryRealty(index)}>
                      <Trash color="secondary.500" />
                      Odebrat
                    </Button>
                  </Flex>}
                </FormGroup>
              </Box>
            </React.Fragment>)}

            <Divider borderColor="gray" />

            {!isContracted && <Box>
              <Button type="button" variant="outline" onClick={() => appendSecondaryRealty(({} as SecondaryRealtyDto))}>
                Přidat další budovu
              </Button>
            </Box>
            }

            {isInsurersAvailable([
              Insurer.KB
            ]) && (
                <FormControl isRequired isInvalid={!!errors.property?.data?.realty?.secondaryBuildingsArea}>
                  <FormLabel tooltip="Souhrnná plocha všech vedlejších staveb">
                    Celková plocha vedlejších budov
                  </FormLabel>

                  <CustomNumberInput w={32} defaultValue={getValues('property.data.realty.secondaryBuildingsArea')} onValueChange={(_, value) => setValue('property.data.realty.secondaryBuildingsArea', value)} isDisabled={isContracted} inputRightElement="㎡" inputProps={{
                    placeholder: 'Např. 128'
                  }} />

                  {errors.property?.data?.realty?.secondaryBuildingsArea && <FormErrorMessage>
                    {errors.property?.data?.realty?.secondaryBuildingsArea.message}
                  </FormErrorMessage>}
                </FormControl>
              )}
          </Box>
        </CustomCollapse>





      </FormSectionBody >

    </FormSection >
  )
}